import React, { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Pencil, Check, Trash2 } from "lucide-react";
import "./Price.css";
import AdditionalCosts from './AdditionalCosts';
import CurrencySettings from "./CurrencySettings";
import { LanguageContext } from "../context/LanguageContext";



// DateField component (füge oben in Price-Datei ein)
const parseFlexibleDate = (str) => {
  if (!str) return null;
  const s = String(str).trim();
  // Accept YYYY-MM-DD, DD.MM.YYYY, DD-MM-YYYY, DD MM YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parts = s.replace(/\./g, '-').replace(/\s+/g, '-').split('-').filter(Boolean);
  if (parts.length === 3) {
    // if parts = [YYYY,MM,DD]
    if (parts[0].length === 4) {
      const y = parts[0], m = parts[1].padStart(2, '0'), d = parts[2].padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    // if parts = [DD,MM,YYYY]
    if (parts[2] && parts[2].length === 4) {
      const y = parts[2], m = parts[1].padStart(2, '0'), d = parts[0].padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  // fallback: try Date parse
  const maybe = new Date(s);
  if (!isNaN(maybe.getTime())) return maybe.toISOString().split('T')[0];
  return null;
};

const formatDisplayDDMMYYYY = (ymd) => {
  if (!ymd) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const [y,m,d] = ymd.split("-");
  return `${d}.${m}.${y}`;
};

const DateField = ({ 
  valueYMD, 
  onChangeYMD, 
  minYMD, 
  placeholder, 
  calendarLabel,
  disabled = false 
}) => {

  // visible text input value (local format)
  const [text, setText] = useState(valueYMD ? formatDisplayDDMMYYYY(valueYMD) : "");
  const [lastYMD, setLastYMD] = useState(valueYMD);

  useEffect(() => {
    // keep display in sync when external value changes
    if (valueYMD !== lastYMD) {
      setText(valueYMD ? formatDisplayDDMMYYYY(valueYMD) : "");
      setLastYMD(valueYMD);
    }
  }, [valueYMD, lastYMD]);

  // ref for native date input to programmatically open (focus)
  const nativeRef = React.useRef(null);

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const handleBlur = () => {
    const parsed = parseFlexibleDate(text);
    if (parsed) {
      // if minYMD present, enforce it (but do not force-correct now; let validate onSave if you prefer)
      if (minYMD && parsed < minYMD) {
        // if parsed < min, clamp to minYMD
        onChangeYMD(minYMD);
        setText(formatDisplayDDMMYYYY(minYMD));
        setLastYMD(minYMD);
        return;
      }
      onChangeYMD(parsed);
      setText(formatDisplayDDMMYYYY(parsed));
      setLastYMD(parsed);
    } else {
      // keep raw text but clear YMD if unparsable
      onChangeYMD("");
      setLastYMD("");
    }
  };

  const handleNativeChange = (e) => {
    const ymd = e.target.value; // native YYYY-MM-DD
    if (!ymd) {
      onChangeYMD("");
      setText("");
      setLastYMD("");
      return;
    }
    onChangeYMD(ymd);
    setText(formatDisplayDDMMYYYY(ymd));
    setLastYMD(ymd);
  };

  const openNativePicker = () => {
    if (nativeRef.current) nativeRef.current.showPicker?.() || nativeRef.current.focus();
  };

  
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        onChange={handleTextChange}
        onBlur={handleBlur}
        disabled={disabled}
        style={{ minWidth: 120 }}
      />
      {/* hidden native date input for picker; not tabbable */}
      <input
        type="date"
        ref={nativeRef}
        value={valueYMD || ""}
        onChange={handleNativeChange}
        min={minYMD || undefined}
        style={{ position: 'absolute', opacity: 0, width: 1, height: 1, border: 'none', padding: 0, margin: 0 }}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={openNativePicker}
        disabled={disabled}
        aria-label={calendarLabel}
        style={{ padding: 6 }}
        tabIndex={-1}
      >
        📅
      </button>
    </div>
  );
};


const Price = () => {
  const { language, texts } = useContext(LanguageContext);
  const { objectId } = useParams();
  const location = useLocation(); // nur EINMAL nötig
  const navigate = useNavigate();
  const [ownerObjects, setOwnerObjects] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);




  // URL-Parameter auslesen
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState("preise");


  const [seasons, setSeasons] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [objectName, setObjectName] = useState("");
  const [showExpiredSeasons, setShowExpiredSeasons] = useState(false);
  const [currencyCode, setCurrencyCode] = useState("CHF");

  const today = new Date().toISOString().split("T")[0];
  const nameRefs = React.useRef([]);


  const formatDateWithoutTZShift = (isoString) => {
    const date = new Date(isoString);
    date.setHours(0, 0, 0, 0);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

    const fetchCurrency = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/object/${objectId}/currency`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error(texts.prices.errorFetchCurrency);

        const data = await response.json();

        if (!data.currency) {
          setCurrencyCode(null);
          setShowCurrencyModal(true);
        } else {
          setCurrencyCode(data.currency);
        }

      } catch (error) {
        console.error(texts.prices.errorFetchCurrency, error);
      }
    };

    const [availableCurrencies, setAvailableCurrencies] = useState([]);

    const saveCurrency = async () => {
  const token = localStorage.getItem("authToken");
  if (!token || !currencyCode) return;

  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/object/${objectId}/currency`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currency: currencyCode }),
      }
    );

    if (!response.ok) {
      alert(texts.currency.errorSaving);
      return;
    }

    // Erfolgreich gespeichert → Modal schliessen + neu laden
    setShowCurrencyModal(false);
    fetchCurrency();

  } catch (error) {
    console.error("Fehler beim Speichern der Währung:", error);
    alert(texts.currency.errorSaving);
  }
};


    useEffect(() => {
      const fetchCurrencies = async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/currencies?lang=${language}`);
        const data = await response.json();
        setAvailableCurrencies(data);
      };

      fetchCurrencies();
    }, [language]);


  
    useEffect(() => {
      const fetchObjectData = async () => {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setObjectName(`${texts.prices.object} ${objectId}`);
          return;
        }

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/objects`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!response.ok) throw new Error(texts.prices.errorFetchObjects);

          const objects = await response.json();

          // aktuelles Objekt finden (robust)
          const matchedObject = objects.find(
            (obj) => String(obj.id) === String(objectId)
          );

          // Name setzen
          setObjectName(
            matchedObject?.name || `${texts.prices.object} ${objectId}`
          );

          // alle Objekte desselben Vermieters
          if (matchedObject) {
            const sameOwner = objects.filter(
              (obj) => obj.owner_id === matchedObject.owner_id
            );
            setOwnerObjects(sameOwner);
          } else {
            // Fallback: wenn matchedObject nicht gefunden wird → alle Objekte anzeigen
            setOwnerObjects(objects);
          }

        } catch (error) {
          console.error(texts.prices.errorFetchObjectName, error);
          setObjectName(`${texts.prices.object} ${objectId}`);
        }
      };

      fetchObjectData();
      fetchCurrency();
    }, [objectId, texts]);



  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/object/${objectId}/seasons`);
        if (!response.ok) {
          if (response.status === 404) {
            setSeasons([]);
            return;
          }
          throw new Error(texts.prices.errorFetchSeasons);
        }
        const data = await response.json();
        const formattedSeasons = data.map(season => ({
          ...season,
        startDate: season.start_date ? formatDateWithoutTZShift(season.start_date) : "",
        endDate: season.end_date ? formatDateWithoutTZShift(season.end_date) : "",
        }));
        const sortedSeasons = formattedSeasons.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
        setSeasons(sortedSeasons);
      } catch (error) {
        console.error(texts.prices.errorFetchSeasons, error);
        alert(texts.prices.errorFetchSeasonsAlert);
      }
    };
    fetchSeasons();
  }, [objectId]);

    const isSeasonExpired = (endDate) => {
      if (!isValidDateString(endDate)) return false;
      return new Date(endDate) < new Date(today);
    };


  const isValidDateString = (s) =>
    typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(new Date(s).getTime());

  const nextDayYMD = (s) => {
    if (!isValidDateString(s)) return null;
    const d = new Date(s);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };


    const validateEndDate = (index) => {
      setSeasons(prev => {
        const copy = [...prev];
        const s = copy[index];
        if (!s) return prev;

        if (!isValidDateString(s.startDate) || !isValidDateString(s.endDate)) return copy;

        const start = new Date(s.startDate);
        const end = new Date(s.endDate);
        if (end <= start) {
          const corrected = new Date(start);
          corrected.setDate(corrected.getDate() + 1);
          copy[index] = { ...s, endDate: corrected.toISOString().split('T')[0] };
        }
        return copy;
      });
    };


    const handleSeasonChange = (index, event) => {
      const { name, value } = event.target;
      setSeasons(prev => {
        const copy = [...prev];
        copy[index] = { ...copy[index], [name]: value };
        return copy;
      });
    };



  const formatDateWithoutTime = (isoString) => {
    const date = new Date(isoString);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const handleSaveSeason = async (index) => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      alert(texts.prices.errorNotLoggedIn);
      return;
    }

    let updatedSeason = { ...seasons[index] };
    if (!updatedSeason.name || !updatedSeason.startDate || !updatedSeason.endDate || !updatedSeason.price) {
      alert(texts.prices.errorMissingFields);
      return;
    }

    const start = new Date(updatedSeason.startDate);
    const end = new Date(updatedSeason.endDate);
    if (end < start) {
      alert(texts.prices.errorEndBeforeStart);
      return;
    }

    const overlaps = seasons.some((season, i) => {
      if (i === index) return false;
      if (!season.startDate || !season.endDate) return false;
      const sStart = new Date(season.startDate);
      let sEnd = new Date(season.endDate);
      sEnd.setDate(sEnd.getDate() - 1);
      return (start < sEnd && end > sStart) || start.getTime() === sEnd.getTime();
    });

    if (overlaps) {
      alert(texts.prices.errorOverlap);
      return;
    }

    const method = updatedSeason.id ? "PUT" : "POST";
    const url = updatedSeason.id
      ? `${process.env.REACT_APP_API_URL}/object/${objectId}/seasons/${updatedSeason.id}`
      : `${process.env.REACT_APP_API_URL}/object/${objectId}/seasons`;

    const requestBody = {
      name: updatedSeason.name,
      start_date: updatedSeason.startDate,
      end_date: updatedSeason.endDate,
      price: updatedSeason.price,
    };

    setLoading(true);

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error(texts.prices.errorSaveSeason);
      const savedData = await response.json();

      const finalSeason = {
        ...savedData,
        name: updatedSeason.name,
        price: updatedSeason.price,
        startDate: savedData?.start_date ? formatDateWithoutTZShift(savedData.start_date) : updatedSeason.startDate,
        endDate: savedData?.end_date ? formatDateWithoutTZShift(savedData.end_date) : updatedSeason.endDate,
      };


      setSeasons(prevSeasons => {
        const updatedSeasons = [...prevSeasons];
        updatedSeasons[index] = finalSeason;
        return updatedSeasons;
      });

    } catch (error) {
      console.error(texts.prices.errorApiRequest, error);
      alert(texts.prices.errorSaveSeasonAlert);
    } finally {
      setLoading(false);
      setEditingIndex(null);
        setTimeout(() => {
          const el = nameRefs.current[index];
          if (el && typeof el.blur === 'function') el.blur();
        }, 0);
    }
  };

  const handleDeleteSeason = async (index) => {
    const authToken = localStorage.getItem("authToken");
    const seasonToDelete = seasons[index];

    if (!seasonToDelete.id) {
      setSeasons(seasons.filter((_, i) => i !== index));
      return;
    }

    const confirmDelete = window.confirm(
  `${texts.prices.confirmDeleteSeason} "${seasonToDelete.name}"?`
);

    if (!confirmDelete) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/object/${objectId}/seasons/${seasonToDelete.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${authToken}`,
        },
      });

      if (!response.ok) throw new Error(texts.prices.errorDeleteSeason);
      setSeasons(seasons.filter((_, i) => i !== index));
    } catch (error) {
      console.error(texts.prices.errorDeleteSeason, error);
      alert(texts.prices.errorDeleteSeasonAlert);
    }
  };

  const handleAddSeason = () => {
    if (editingIndex !== null) {
      alert(texts.prices.errorFinishEditingFirst);
      return;
    }
    setSeasons([...seasons, { name: "", price: "", startDate: "", endDate: "" }]);
    setEditingIndex(seasons.length);
  };

  const toggleExpiredSeasons = () => {
    setShowExpiredSeasons(prev => !prev);
  };

  const isOnNebenkostenPage = location.pathname.includes("nebenkosten");

  return (
    <div className="price-availability-container">
      <div className="tabs" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <button
          className={`tab-button ${activeTab === 'preise' ? 'active' : ''}`}
          onClick={() => setActiveTab('preise')}
        >
          {texts.prices.tabPrices}
        </button>

        <button
          className={`tab-button ${activeTab === 'nebenkosten' ? 'active' : ''}`}
          onClick={() => setActiveTab('nebenkosten')}
        >
          {texts.prices.tabAdditionalCosts}
        </button>

        <button
          className={`tab-button ${activeTab === 'waehrung' ? 'active' : ''}`}
          onClick={() => setActiveTab('waehrung')}
        >
         {texts.prices.tabCurrency}
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: "center" }}>

        {ownerObjects.length > 1 && (
          <select
            value={objectId}
            onChange={(e) => navigate(`/prices/${e.target.value}?tab=${activeTab}`)}
            className="object-switcher"
          >
            {ownerObjects.map(obj => (
              <option key={obj.id} value={obj.id}>
                {obj.name || `${texts.prices.object} ${obj.id}`}
              </option>
            ))}
          </select>
        )}

         <button
            type="button"
            className="tab-button"
            onClick={() => navigate(`/availability/${objectId}`)}
          >
            {texts.prices.tabAvailability}
          </button>

          <button
            type="button"
            className="tab-button"
            onClick={() => navigate('/object-list')}
          >
            {texts.prices.tabObjectList}
          </button>
        </div>

      </div>

      {activeTab === "preise" && (
        <div className="tab-content">
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            marginBottom: 12 
          }}>
            <h2 style={{ margin: 0 }}>
              {texts.prices.pricesFor} {objectName}
            </h2>

            <button 
              type="button"
              className="info-circle-button"
              onClick={() => setShowInfo(true)}
            >
              ?
            </button>
          </div>

          <button onClick={toggleExpiredSeasons} className="toggle-expired-button">
            {showExpiredSeasons 
              ? texts.prices.hidePastSeasons 
              : texts.prices.showPastSeasons
            }

          </button>

          <div className="season-list">
            {seasons.map((season, index) => {
              const isExpired = isSeasonExpired(season.endDate);
              const shouldDisplay = !isExpired || showExpiredSeasons;

              if (!shouldDisplay) return null;

              return (
                <div key={index} className="season-item">
                <input
                  ref={el => nameRefs.current[index] = el}
                  type="text"
                  name="name"
                  value={season.name ?? ""}
                  onChange={(e) => handleSeasonChange(index, e)}
                  placeholder={texts.prices.seasonName}
                  required
                  disabled={editingIndex !== index || isExpired}
                />
                <DateField
                  valueYMD={season.startDate || ""}
                  onChangeYMD={(ymd) => {
                    handleSeasonChange(index, { target: { name: "startDate", value: ymd } });
                  }}
                  minYMD={today}
                  disabled={editingIndex !== index || isExpired}
                  placeholder={texts.prices.datePlaceholder}
                  calendarLabel={texts.prices.openCalendar}
                />
                <DateField
                  valueYMD={season.endDate || ""}
                  onChangeYMD={(ymd) => {
                    handleSeasonChange(index, { target: { name: "endDate", value: ymd } });
                  }}
                  minYMD={isValidDateString(season.startDate) ? nextDayYMD(season.startDate) : undefined}
                  disabled={editingIndex !== index || isExpired}
                  placeholder={texts.prices.datePlaceholder}
                  calendarLabel={texts.prices.openCalendar}
                />

                  {editingIndex === index && !isExpired ? (
                  <input
                    type="number"
                    name="price"
                    value={season.price ?? ""}
                    onChange={(e) => handleSeasonChange(index, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // optional: ensure endDate normalized before save
                        validateEndDate(index);
                        handleSaveSeason(index);
                      }
                    }}
                    placeholder={`${texts.prices.pricePerNight} ${currencyCode}`}
                    required
                    disabled={editingIndex !== index || isExpired}
                  />
                  ) : (
                    <input
                      type="text"
                      className="number-input"
                      value={season.price ? `${parseFloat(season.price).toFixed(2)} ${currencyCode}` : ""}
                      disabled
                    />
                  )}

                  <div className="button-container">
                  {editingIndex === index && !isExpired && (
                    <>
                      <button
                        type="button"
                        className="save-button"
                        onClick={() => handleSaveSeason(index)}
                        disabled={loading}
                      >
                        {loading ? texts.prices.saving : <Check size={18} strokeWidth={4} color="#f7f4f4ff" />}
                      </button>

                      <button
                        type="button"
                        className="cancel-button"
                        onClick={() => {
                          if (!seasons[index].id) {
                            setSeasons(seasons.filter((_, i) => i !== index));
                          }
                          setEditingIndex(null);
                        }}
                      >
                        {texts.prices.cancel}
                      </button>
                    </>
                  )}
                  </div>

                  <div className="button-container">
                  {!isExpired && editingIndex === null && (
                    <>
                      <button
                        type="button"
                        className="edit-button"
                        onClick={() => setEditingIndex(index)}
                      >
                        <Pencil size={18} strokeWidth={1.5} color="#000" />
                      </button>

                      <button
                        type="button"
                        className="delete-button"
                        onClick={() => handleDeleteSeason(index)}
                      >
                        <Trash2 size={18} strokeWidth={1.5} color="#cc0000" />
                      </button>
                    </>
                  )}
                   </div>
                </div>
              );
            })}
          </div>

          {!isOnNebenkostenPage && (
            <button type="button" className="add-button" onClick={handleAddSeason} disabled={editingIndex !== null}>
              {texts.prices.addSeason}
            </button>
          )}
        </div>
      )}

      {activeTab === "nebenkosten" && (
        <div className="tab-content">
          <AdditionalCosts objectId={objectId} />
        </div>
      )}
      {activeTab === "waehrung" && (
        <div className="tab-content">
          <CurrencySettings objectId={objectId} onCurrencyChange={fetchCurrency} />
        </div>
      )}

      {showInfo && (
        <div className="modal-overlay" onClick={() => setShowInfo(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{texts.prices.infoTitle}</h2>
            <p>{texts.prices.infoText}</p>

            <button
              type="button"
              className="tab-button"
              onClick={() => setShowInfo(false)}
            >
              {texts.prices.close}
            </button>
          </div>
        </div>
      )}

{showCurrencyModal && (
  <div className="currency-modal-overlay" onClick={() => {}}>
    <div className="currency-modal-box" onClick={(e) => e.stopPropagation()}>
      <h2>{texts.prices.selectCurrencyTitle}</h2>
      <p>{texts.prices.selectCurrencyText}</p>

      <div className="currency-modal-content">
        <label>{texts.currency.title}</label>
        <select
          value={currencyCode || ""}
          onChange={(e) => setCurrencyCode(e.target.value)}
        >
          <option value="">{texts.currency.select}</option>
          {availableCurrencies.map((cur) => (
            <option key={cur.code} value={cur.code}>
              {cur.name} ({cur.code})
            </option>
          ))}
        </select>

        <button
          className="object-button"
          disabled={!currencyCode}
          onClick={saveCurrency}
        >
          {texts.currency.save}
        </button>
      </div>
    </div>
  </div>
)}


    </div> 
  );
};

export default Price;

